import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { reviewRequestsApi, notificationsApi } from '../services/api';
import { formatDate, formatDateWithMonth } from '../lib/dateUtils';
import { refreshNotificationCounts } from '../lib/notificationUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  MessageSquare,
  User,
  Calendar,
  Building2,
  ArrowLeft,
  Filter,
  Search,
  TrendingUp,
  TrendingDown,
  AlertTriangle
} from 'lucide-react';

const MONTHS = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

const ApprovalsPage = () => {
  const { user, isAdmin } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewDialog, setReviewDialog] = useState({ open: false, request: null });
  const [cfoComment, setCfoComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCFO = user?.is_cfo || isAdmin();

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await reviewRequestsApi.getAll(params);
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading review requests:', error);
      toast.error('Failed to load review requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (status) => {
    if (!reviewDialog.request) return;
    
    try {
      setSubmitting(true);
      await reviewRequestsApi.update(reviewDialog.request.id, {
        status,
        cfo_comment: cfoComment
      });
      
      toast.success(`Request ${status === 'accepted' ? 'accepted' : 'rejected'} successfully`);
      setReviewDialog({ open: false, request: null });
      setCfoComment('');
      loadRequests();
      
      // Trigger refresh of notification counts in Layout
      refreshNotificationCounts();
    } catch (error) {
      console.error('Error reviewing request:', error);
      toast.error('Failed to update request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'accepted':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300"><CheckCircle2 className="w-3 h-3 mr-1" /> Accepted</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R 0.00';
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absValue);
    return value < 0 ? `(${formatted})` : formatted;
  };

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.account_name?.toLowerCase().includes(query) ||
      req.account_code?.toLowerCase().includes(query) ||
      req.business_unit_name?.toLowerCase().includes(query) ||
      req.submitted_by_name?.toLowerCase().includes(query) ||
      req.reason?.toLowerCase().includes(query)
    );
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  return (
    <div className={`p-6 ${isDark ? 'bg-zinc-950 text-white' : 'bg-slate-50 text-slate-900'}`} data-testid="approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className={isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100'}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Approvals</h1>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
              Review and manage financial review requests
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div 
          className={`p-4 rounded-lg border cursor-pointer transition-colors ${
            statusFilter === 'pending' 
              ? isDark ? 'bg-amber-900/20 border-amber-600' : 'bg-amber-50 border-amber-300'
              : isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
          data-testid="pending-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Pending Review</p>
              <p className="text-3xl font-bold text-amber-500">{pendingCount}</p>
            </div>
            <Clock className="w-10 h-10 text-amber-500 opacity-50" />
          </div>
        </div>
        
        <div 
          className={`p-4 rounded-lg border cursor-pointer transition-colors ${
            statusFilter === 'accepted' 
              ? isDark ? 'bg-green-900/20 border-green-600' : 'bg-green-50 border-green-300'
              : isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'accepted' ? 'all' : 'accepted')}
          data-testid="accepted-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Accepted</p>
              <p className="text-3xl font-bold text-green-500">{acceptedCount}</p>
            </div>
            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
          </div>
        </div>
        
        <div 
          className={`p-4 rounded-lg border cursor-pointer transition-colors ${
            statusFilter === 'rejected' 
              ? isDark ? 'bg-red-900/20 border-red-600' : 'bg-red-50 border-red-300'
              : isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'rejected' ? 'all' : 'rejected')}
          data-testid="rejected-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Rejected</p>
              <p className="text-3xl font-bold text-red-500">{rejectedCount}</p>
            </div>
            <XCircle className="w-10 h-10 text-red-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`p-4 rounded-lg border mb-6 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-zinc-400' : 'text-slate-400'}`} />
            <Input
              placeholder="Search by account, BU, or submitter..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="search-input"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={`w-[180px] ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Requests List */}
      <div className={`rounded-lg border ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200'}`}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>Loading requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-8 text-center">
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
            <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>No review requests found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {filteredRequests.map((request) => (
              <div 
                key={request.id}
                className={`p-4 hover:${isDark ? 'bg-zinc-800/50' : 'bg-slate-50'} transition-colors cursor-pointer`}
                onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
                data-testid={`request-${request.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(request.status)}
                      <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {request.month?.toUpperCase()} • {request.business_unit_name}
                      </span>
                    </div>
                    
                    <h3 className="font-semibold mb-1">
                      {request.account_code} - {request.account_name}
                    </h3>
                    
                    <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                      <span className="font-medium">Reason:</span> {request.reason}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className={isDark ? 'text-zinc-500' : 'text-slate-500'}>
                        <User className="w-3 h-3 inline mr-1" />
                        {request.submitted_by_name}
                      </span>
                      <span className={isDark ? 'text-zinc-500' : 'text-slate-500'}>
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDateWithMonth(request.submitted_at, { includeTime: true })}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>Budget</p>
                        <p className="font-medium">{formatCurrency(request.budget_amount)}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>Actual</p>
                        <p className="font-medium">{formatCurrency(request.actual_amount)}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>Variance</p>
                        <p className={`font-medium ${request.variance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {request.variance >= 0 ? <TrendingUp className="w-3 h-3 inline" /> : <TrendingDown className="w-3 h-3 inline" />}
                          {' '}{formatCurrency(Math.abs(request.variance))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {selectedRequest?.id === request.id && (
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Comment</p>
                        <p className={`text-sm p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                          {request.comment || '-'}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Suggested Action</p>
                        <p className={`text-sm p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                          {request.suggested_action || '-'}
                        </p>
                      </div>
                    </div>
                    
                    {request.status !== 'pending' && (
                      <div className={`p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                          CFO Response ({request.reviewed_by_name})
                        </p>
                        <p className="text-sm">{request.cfo_comment || 'No comment provided'}</p>
                        <p className={`text-xs mt-2 ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                          Reviewed: {formatDateWithMonth(request.reviewed_at, { includeTime: true })}
                        </p>
                      </div>
                    )}
                    
                    {request.status === 'pending' && isCFO && (
                      <div className="flex justify-end gap-2 mt-4">
                        <Button
                          variant="outline"
                          className="text-red-500 border-red-500 hover:bg-red-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReviewDialog({ open: true, request });
                            setCfoComment('');
                          }}
                          data-testid="reject-button"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReviewDialog({ open: true, request });
                            setCfoComment('');
                          }}
                          data-testid="accept-button"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Accept
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ open, request: reviewDialog.request })}>
        <DialogContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
          <DialogHeader>
            <DialogTitle>Review Request</DialogTitle>
            <DialogDescription>
              {reviewDialog.request?.account_name} - {reviewDialog.request?.month?.toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                CFO Comment (Optional)
              </label>
              <Textarea
                placeholder="Add a comment explaining your decision..."
                value={cfoComment}
                onChange={(e) => setCfoComment(e.target.value)}
                className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                rows={4}
                data-testid="cfo-comment-input"
              />
            </div>
            
            <div className={`p-3 rounded text-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
              <p className="font-medium mb-1">Request Details:</p>
              <p><strong>Reason:</strong> {reviewDialog.request?.reason}</p>
              <p><strong>Comment:</strong> {reviewDialog.request?.comment}</p>
              {reviewDialog.request?.suggested_action && (
                <p><strong>Suggested Action:</strong> {reviewDialog.request?.suggested_action}</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewDialog({ open: false, request: null })}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-red-500 border-red-500 hover:bg-red-500/10"
              onClick={() => handleReview('rejected')}
              disabled={submitting}
              data-testid="confirm-reject-button"
            >
              {submitting ? 'Processing...' : 'Reject'}
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => handleReview('accepted')}
              disabled={submitting}
              data-testid="confirm-accept-button"
            >
              {submitting ? 'Processing...' : 'Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
